import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "../../i18n";
import { Chip } from "./shared";
import { HeroMock } from "./mocks";

// Static animation presets (defined once, not re-created per render).
const EASE_OUT = [0.22, 1, 0.36, 1];
const ANIM_BADGE = { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5 } };
const ANIM_H1 = { initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.55, delay: 0.05 } };
const ANIM_SUB = { initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.55, delay: 0.12 } };
const ANIM_CTA = { initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.55, delay: 0.18 } };
const ANIM_MOCK = { initial: { opacity: 0, y: 22 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6, delay: 0.15, ease: EASE_OUT } };
const AVATAR_COLORS = ["#2563eb", "#0b1b3a", "#16a34a"];

export const Hero = ({ waitlistCount }) => {
  const { t } = useLang();
  return (
    <section id="top" className="relative overflow-hidden hero-wash">
      <div className="relative mx-auto grid max-w-[1180px] grid-cols-1 gap-12 px-4 pt-28 pb-16 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-8 lg:pt-36 lg:pb-24">
        <div className="lg:col-span-7 lg:pt-6">
          <motion.span {...ANIM_BADGE}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--border)] bg-white px-3 py-1 text-xs font-semibold text-[var(--blue)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--blue)]" />{t.hero.badge}
          </motion.span>
          <motion.h1 {...ANIM_H1}
            className="font-heading mt-5 text-[1.6rem] font-extrabold leading-[1.14] tracking-tight text-[var(--ink)] [hyphens:none] [overflow-wrap:normal] sm:text-[2.4rem] lg:text-[2.65rem] xl:text-[3rem]">
            {t.hero.line1}<br />{t.hero.line2}<br /><span className="text-[var(--blue)]">{t.hero.line3}</span>
          </motion.h1>
          <motion.p {...ANIM_SUB}
            className="mt-6 max-w-md text-base leading-relaxed text-[var(--slate)] sm:text-lg">{t.hero.subline}</motion.p>
          <motion.div {...ANIM_CTA}
            className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
            <a href="#early-access" data-testid="hero-primary-cta-button" className="group inline-flex items-center justify-center gap-2 rounded-full bg-[var(--blue)] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[var(--blue-2)]">{t.cta.primary}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2} /></a>
            <a href="#tenders" data-testid="hero-secondary-cta-button" className="inline-flex items-center justify-center rounded-full border border-[var(--border)] bg-white px-6 py-3 text-sm font-semibold text-[var(--ink)] transition-colors hover:bg-[var(--surface-2)]">{t.cta.secondary}</a>
          </motion.div>
          <div className="mt-7 flex flex-wrap gap-2">{t.hero.chips.map((c) => <Chip key={c}>{c}</Chip>)}</div>
          {waitlistCount > 0 && (
            <div className="mt-6 flex items-center gap-2 text-sm text-[var(--slate)]" data-testid="waitlist-count-container">
              <span className="flex -space-x-1.5">{AVATAR_COLORS.map((c) => <span key={c} className="h-5 w-5 rounded-full ring-2 ring-white" style={{ background: c }} />)}</span>
              <span>{t.waitlistPrefix}: <strong className="font-semibold text-[var(--ink)]" data-testid="waitlist-count">{waitlistCount}</strong></span>
            </div>
          )}
        </div>
        <div className="lg:col-span-5">
          <motion.div {...ANIM_MOCK}><HeroMock /></motion.div>
        </div>
      </div>
    </section>
  );
};
